import { z } from 'zod';

export interface TimeSlotsApi {
  id: string;
  externalId: string;
  code?: string;
  dateTimeFrom: string;
  dateTimeTo: string;
  logisticOperatorId: string;
  expiration?: string;
  active: boolean;
  mo: boolean;
  tu: boolean;
  we: boolean;
  th: boolean;
  fr: boolean;
  sa: boolean;
  su: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TimeSlots {
  id: string;
  externalId: string;
  code?: string;
  dateTimeFrom: string;
  dateTimeTo: string;
  logisticOperatorId: string;
  expiration?: string;
  active: boolean;
  mo: boolean;
  tu: boolean;
  we: boolean;
  th: boolean;
  fr: boolean;
  sa: boolean;
  su: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TimeSlotsFilter {
  code?: string;
  dateTimeFrom?: string;
  dateTimeTo?: string;
  logisticOperatorId?: string;
  includeAncestors?: boolean;
  expiration?: string;
  active?: boolean;
  mo?: boolean;
  tu?: boolean;
  we?: boolean;
  th?: boolean;
  fr?: boolean;
  sa?: boolean;
  su?: boolean;
}

export interface NewTimeSlots {
  code?: string;
  dateTimeFrom: string;
  dateTimeTo: string;
  logisticOperatorId: string;
  expiration?: string;
  active: boolean;
  mo: boolean;
  tu: boolean;
  we: boolean;
  th: boolean;
  fr: boolean;
  sa: boolean;
  su: boolean;
}

export interface UpdateTimeSlots {
  id: string;
  code?: string;
  dateTimeFrom?: string;
  dateTimeTo?: string;
  logisticOperatorId?: string;
  expiration?: string;
  active?: boolean;
  mo?: boolean;
  tu?: boolean;
  we?: boolean;
  th?: boolean;
  fr?: boolean;
  sa?: boolean;
  su?: boolean;
}

const timeFormatRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

export const timeSlotsFormSchema = z
  .object({
    code: z
      .string()
      .max(50, 'El código no puede exceder 50 caracteres')
      .optional(),
    dateTimeFrom: z
      .string()
      .min(1, 'La hora de inicio es requerida')
      .regex(timeFormatRegex, 'El formato debe ser HH:mm (ej: 08:30)'),
    dateTimeTo: z
      .string()
      .min(1, 'La hora de fin es requerida')
      .regex(timeFormatRegex, 'El formato debe ser HH:mm (ej: 17:30)'),
    expiration: z
      .date()
      .optional()
      .nullable()
      .refine(
        (date) => {
          if (!date) return true;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return date >= today;
        },
        {
          message: 'La fecha de caducidad debe ser futura',
        }
      ),
    active: z.boolean(),
    mo: z.boolean(),
    tu: z.boolean(),
    we: z.boolean(),
    th: z.boolean(),
    fr: z.boolean(),
    sa: z.boolean(),
    su: z.boolean(),
  })
  .refine(
    (data) => {
      if (!data.dateTimeFrom || !data.dateTimeTo) return true;
      const timeFrom = data.dateTimeFrom.split(':').map(Number);
      const timeTo = data.dateTimeTo.split(':').map(Number);
      const fromMinutes = timeFrom[0] * 60 + timeFrom[1];
      const toMinutes = timeTo[0] * 60 + timeTo[1];
      return fromMinutes < toMinutes;
    },
    {
      message: 'La hora de fin debe ser mayor que la hora de inicio',
      path: ['dateTimeTo'],
    }
  )
  .refine(
    (data) => {
      return (
        data.mo ||
        data.tu ||
        data.we ||
        data.th ||
        data.fr ||
        data.sa ||
        data.su
      );
    },
    {
      message: 'Debe seleccionar al menos un día de la semana',
      path: ['mo'],
    }
  );

export const timeSlotsSchema = z
  .object({
    code: z
      .string()
      .max(50, 'El código no puede exceder 50 caracteres')
      .optional(),
    dateTimeFrom: z
      .string()
      .min(1, 'La hora de inicio es requerida')
      .regex(timeFormatRegex, 'El formato debe ser HH:mm (ej: 08:30)'),
    dateTimeTo: z
      .string()
      .min(1, 'La hora de fin es requerida')
      .regex(timeFormatRegex, 'El formato debe ser HH:mm (ej: 17:30)'),
    logisticOperatorId: z
      .string()
      .min(1, 'El operador logístico es requerido')
      .uuid('ID de operador logístico inválido'),
    expiration: z
      .string()
      .optional()
      .refine((val) => {
        if (!val || val === '') return true;
        const date = new Date(val);
        if (isNaN(date.getTime())) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date >= today;
      }, 'La fecha de caducidad debe ser futura y válida'),
    active: z.boolean(),
    mo: z.boolean(),
    tu: z.boolean(),
    we: z.boolean(),
    th: z.boolean(),
    fr: z.boolean(),
    sa: z.boolean(),
    su: z.boolean(),
  })
  .refine(
    (data) => {
      if (!data.dateTimeFrom || !data.dateTimeTo) return true;
      const timeFrom = data.dateTimeFrom.split(':').map(Number);
      const timeTo = data.dateTimeTo.split(':').map(Number);
      const fromMinutes = timeFrom[0] * 60 + timeFrom[1];
      const toMinutes = timeTo[0] * 60 + timeTo[1];
      return fromMinutes < toMinutes;
    },
    {
      message: 'La hora de fin debe ser mayor que la hora de inicio',
      path: ['dateTimeTo'],
    }
  )
  .refine(
    (data) => {
      return (
        data.mo ||
        data.tu ||
        data.we ||
        data.th ||
        data.fr ||
        data.sa ||
        data.su
      );
    },
    {
      message: 'Debe seleccionar al menos un día de la semana',
      path: ['mo'],
    }
  );

export const updateTimeSlotsSchema = z
  .object({
    id: z.string().uuid(),
    code: z
      .string()
      .max(50, 'El código no puede exceder 50 caracteres')
      .optional(),
    dateTimeFrom: z
      .string()
      .min(1, 'La hora de inicio es requerida')
      .regex(timeFormatRegex, 'El formato debe ser HH:mm (ej: 08:30)')
      .optional(),
    dateTimeTo: z
      .string()
      .min(1, 'La hora de fin es requerida')
      .regex(timeFormatRegex, 'El formato debe ser HH:mm (ej: 17:30)')
      .optional(),
    logisticOperatorId: z
      .string()
      .min(1, 'El operador logístico es requerido')
      .uuid('ID de operador logístico inválido')
      .optional(),
    expiration: z
      .string()
      .optional()
      .refine((val) => {
        if (!val || val === '') return true;
        const date = new Date(val);
        if (isNaN(date.getTime())) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date >= today;
      }, 'La fecha de caducidad debe ser futura y válida'),
    active: z.boolean().optional(),
    mo: z.boolean().optional(),
    tu: z.boolean().optional(),
    we: z.boolean().optional(),
    th: z.boolean().optional(),
    fr: z.boolean().optional(),
    sa: z.boolean().optional(),
    su: z.boolean().optional(),
  })
  .refine(
    (data) => {
      if (!data.dateTimeFrom || !data.dateTimeTo) return true;
      const timeFrom = data.dateTimeFrom.split(':').map(Number);
      const timeTo = data.dateTimeTo.split(':').map(Number);
      const fromMinutes = timeFrom[0] * 60 + timeFrom[1];
      const toMinutes = timeTo[0] * 60 + timeTo[1];
      return fromMinutes < toMinutes;
    },
    {
      message: 'La hora de fin debe ser mayor que la hora de inicio',
      path: ['dateTimeTo'],
    }
  )
  .refine(
    (data) => {
      if (
        data.mo === undefined &&
        data.tu === undefined &&
        data.we === undefined &&
        data.th === undefined &&
        data.fr === undefined &&
        data.sa === undefined &&
        data.su === undefined
      )
        return true;
      return (
        data.mo ||
        data.tu ||
        data.we ||
        data.th ||
        data.fr ||
        data.sa ||
        data.su
      );
    },
    {
      message: 'Debe seleccionar al menos un día de la semana',
      path: ['mo'],
    }
  );

export type TimeSlotsFormSchema = z.infer<typeof timeSlotsFormSchema>;
export type TimeSlotsSchema = z.infer<typeof timeSlotsSchema>;
export type UpdateTimeSlotsSchema = z.infer<typeof updateTimeSlotsSchema>;

export const defaultTimeSlotsFormValues: TimeSlotsFormSchema = {
  code: '',
  dateTimeFrom: '08:00',
  dateTimeTo: '17:00',
  expiration: null,
  active: true,
  mo: true,
  tu: true,
  we: true,
  th: true,
  fr: true,
  sa: false,
  su: false,
};

export const DAYS_OF_WEEK = [
  {
    key: 'mo' as keyof Pick<
      TimeSlots,
      'mo' | 'tu' | 'we' | 'th' | 'fr' | 'sa' | 'su'
    >,
    label: 'Lunes',
    short: 'L',
  },
  {
    key: 'tu' as keyof Pick<
      TimeSlots,
      'mo' | 'tu' | 'we' | 'th' | 'fr' | 'sa' | 'su'
    >,
    label: 'Martes',
    short: 'M',
  },
  {
    key: 'we' as keyof Pick<
      TimeSlots,
      'mo' | 'tu' | 'we' | 'th' | 'fr' | 'sa' | 'su'
    >,
    label: 'Miércoles',
    short: 'X',
  },
  {
    key: 'th' as keyof Pick<
      TimeSlots,
      'mo' | 'tu' | 'we' | 'th' | 'fr' | 'sa' | 'su'
    >,
    label: 'Jueves',
    short: 'J',
  },
  {
    key: 'fr' as keyof Pick<
      TimeSlots,
      'mo' | 'tu' | 'we' | 'th' | 'fr' | 'sa' | 'su'
    >,
    label: 'Viernes',
    short: 'V',
  },
  {
    key: 'sa' as keyof Pick<
      TimeSlots,
      'mo' | 'tu' | 'we' | 'th' | 'fr' | 'sa' | 'su'
    >,
    label: 'Sábado',
    short: 'S',
  },
  {
    key: 'su' as keyof Pick<
      TimeSlots,
      'mo' | 'tu' | 'we' | 'th' | 'fr' | 'sa' | 'su'
    >,
    label: 'Domingo',
    short: 'D',
  },
];
